import React, { useMemo } from "react";
import { useQuery } from "react-query";

// apis
import { Fulfillment } from "@secondcloset/types";
import { ToBeDeliveredItemsCard, RemovedItemsCard, RemovedItemsCardV2 } from "@secondcloset/web-components";
import { ToBeDeliveredItemsTable } from "@secondcloset/web-components/dist/components/ToBeDeliveredItemsTable";
// components

// styles
import { Button, Space, Typography } from "antd";
import { isEmpty, orderBy, startCase } from "lodash-es";

import { Container, ShipmentCardWrap, ShipmentSectionWrap, RemovedItemsWrap } from "./styles";

import { fetchUserRole } from "api/accounts/roles";

//types
import { useFacilityIndex } from "../../../../hooks/api/common/useFacilityIndex";
import Organization from "../../../../lib/organization";

import { ShipmentCard } from "./ShipmentCard";
import useUser from "hooks/reduxContainer/_common/useUser";

type Version = "V1" | "V2";
type Order<V extends Version> = Fulfillment.Order<V>;
type Shipment<V extends Version> = Fulfillment.Shipment<V>;
type FulfillmentShipmentType = Fulfillment.AppointmentType | "customer_pickup";

interface Props {
  version: Version;
  order?: Order<Version>;
  orderItems?: Fulfillment.OrderItem<"V2">[];
  workOrders?: Record<string, Record<string, string>>;
  shipments: Shipment<Version>[];
  orderLoading: boolean;
  itemsLoading: boolean;
  shipmentsLoading: boolean;
  onRefetch: () => unknown;
  onShipItemsClick: () => unknown;
}

const OrderShipments: React.FC<Props> = ({
  version = "V1",
  order,
  orderItems = [],
  shipments,
  workOrders,
  orderLoading,
  itemsLoading,
  shipmentsLoading,
  onRefetch,
  onShipItemsClick,
}) => {
  const isIkea = Organization.isIKEA(order?.organization);
  const facilityIndex = useFacilityIndex({ enabled: !isEmpty(shipments) });
  const {
    user: { organization_role_id: orgRoleID },
  } = useUser();

  const role = useQuery(["fetchUserRole", orgRoleID], () => fetchUserRole(orgRoleID), {
    enabled: !!orgRoleID,
  });

  const hasResetShipmentPermission = useMemo(() => {
    return !!role.data?.permissions.find((permission) => permission.name === "manage_reset_shipments");
  }, [role.data]);

  const renderShipments = (title: string, shipments: Shipment<Version>[]) => {
    if (shipments.length === 0) return null;
    return (
      <ShipmentSectionWrap key={title}>
        <Typography.Title level={4}>{title}</Typography.Title>
        {orderBy(shipments, ["updated_at"], ["desc"]).map((shipment, index) => (
          <ShipmentCardWrap key={`${shipment.id}-${index}`} id={shipment.id}>
            <ShipmentCard
              //TODO fix wc shipment card props to handle v2
              order={order as Order<"V1">}
              shipment={shipment as Shipment<"V1">}
              isLoading={orderLoading || shipmentsLoading}
              workOrders={workOrders}
              onRefetch={onRefetch}
              facilities={facilityIndex.data || []}
              userHasResetShipmentPermission={hasResetShipmentPermission}
            />
          </ShipmentCardWrap>
        ))}
      </ShipmentSectionWrap>
    );
  };

  const renderUntrackedShipments = () => {
    const untrackedShipments = shipments.filter((shipment) => shipment?.shipping_method_type === "untracked_shipment");
    return renderShipments("Untracked Delivery Shipments", untrackedShipments as Shipment<Version>[]);
  };

  const renderAllShipmentTypes = () => {
    const shipmentTypes: FulfillmentShipmentType[] = [
      "reverse_logistics",
      "delivery",
      "cross_dock_delivery",
      "inventory_pick_up",
      "return_to_sender",
      "inventory_warehouse_receiving",
      "customer_pickup",
    ];

    return shipmentTypes.map((type, index) => {
      const shipment = shipments.filter(
        (s) => s?.shipping_method_type !== "untracked_shipment" && s?.action_summary?.includes(type)
      );
      const title = type === "cross_dock_delivery" ? "Delivery" : startCase(type);
      return (
        <React.Fragment key={`${type}-${index}`}>
          {renderShipments(`${title} Shipments (${shipment?.length})`, shipment)}
        </React.Fragment>
      );
    });
  };

  const goToProductDetailsPage = (productID: string) => {
    window.open(`/organizations/${order?.organization?.id}/products/${productID}`);
  };

  return (
    <Container>
      <br />
      {version === "V1" ? (
        <>
          <ToBeDeliveredItemsCard
            order={order as Order<"V1">}
            onProductClick={goToProductDetailsPage}
            onShipItemsClick={onShipItemsClick}
            isIkea={isIkea}
            isAdmin={true}
          />
          <RemovedItemsWrap>
            <RemovedItemsCard order={order as Order<"V1">} onProductClick={goToProductDetailsPage} />
          </RemovedItemsWrap>
        </>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <ToBeDeliveredItemsTable
            loading={itemsLoading}
            order={order as Order<"V2">}
            orderItems={orderItems}
            shipments={shipments}
            renderSCIDLink={(id, scid) => (
              <Typography.Link target="_blank" href={`/organizations/${order?.organization?.id}/products/${id}`}>
                {scid}
              </Typography.Link>
            )}
            isIkea={isIkea}
            isAdmin={true}
            onShipItemsClick={onShipItemsClick}
          />
          <RemovedItemsCardV2 orderItems={orderItems} onProductClick={goToProductDetailsPage} />
        </Space>
      )}

      {renderUntrackedShipments()}
      {renderAllShipmentTypes()}
    </Container>
  );
};

export { OrderShipments };
